import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {map} from "rxjs/operators";
import {ApiService} from "../api/api.service";
import moment from 'moment-timezone';
import {PermissionsService} from "../permissions/permissions.service";

@Injectable({
  providedIn: 'root'
})

export class InsightsService {

  tz = moment.tz.guess();

  constructor(private api: ApiService, private permissionService: PermissionsService) {}

  private static getQueryStr(start: any, end: any, questionnaire: any, tz: string, filterBy: any): string {
    start = moment.unix(start.unix()).locale('en-au');
    end = moment.unix(end.unix()).locale('en-au');
    if (questionnaire !== '') {
      return 'start=' + start.format('DD-MMM-YYYY')
        + '&end=' + end.format('DD-MMM-YYYY')
        + '&questionnaire=' + questionnaire
        + '&tz=' + tz
        + '&filterBy=' + filterBy;
    } else {
      return 'start=' + start.format('DD-MMM-YYYY')
        + '&end=' + end.format('DD-MMM-YYYY')
        + '&tz=' + tz
        + '&filterBy=' + filterBy;
    }
  }

  private static getPulseQueryStr(
    start: any,
    end: any,
    questionnaire: any,
    tz: string,
    department: any,
    request: any,
    filterBy?: any,
    departmentCategory?: any,
  ): string {
    start = moment.unix(start.unix()).locale('en-au');
    end = moment.unix(end.unix()).locale('en-au');
    let params = 'start=' + start.format('DD-MMM-YYYY')
      + '&end=' + end.format('DD-MMM-YYYY')
      + '&tz=' + tz
    if (questionnaire != '') {
      params += '&questionnaire=' + questionnaire
    }
    if (department != '') {
      params += '&department=' + department
    }
    if(departmentCategory != '') {
      params += '&department_category=' + departmentCategory
    }
    if (request != '') {
      params += '&request=' + request
    }
    if (filterBy != '' ) {
      params += '&filterBy=' + filterBy
    }
    return params
  }

  private static getPulseCommentsQueryStr(
    start: any,
    end: any,
    questionnaire: any,
    tz: string,
    department: any,
    request: any,
    limit: any,
    page:any
  ): string {
    start = moment.unix(start.unix()).locale('en-au');
    end = moment.unix(end.unix()).locale('en-au');
    let params = 'start=' + start.format('DD-MMM-YYYY')
      + '&end=' + end.format('DD-MMM-YYYY')
      + '&tz=' + tz
    if (questionnaire != '') {
      params += '&questionnaire=' + questionnaire
    }
    if (department != '') {
      params += '&department=' + department
    }
    if (request != '') {
      params += '&request=' + request
    }
    if(limit != 0) {
      params += '&limit=' + limit
      params += '&offset=' + limit*(page - 1)
    }
    return params
  }

  getNPSOverall(start: any, end: any, questionnaire: any, filterBy: any): Observable<any> {
    let endpoint = 'insights/exits/nps/overall?' + InsightsService.getQueryStr(start, end, questionnaire, this.tz, filterBy);
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  getNPSTrend(start: any, end: any, questionnaire: any, filterBy: any): Observable<any> {
    let endpoint = 'insights/exits/nps/trend?'
        + InsightsService.getQueryStr(start, end, questionnaire, this.tz, filterBy)
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  getNPSCategory(start: any, end: any, questionnaire: any, filterBy: any): Observable<any> {
    let endpoint = 'insights/exits/nps/category?'
        + InsightsService.getQueryStr(start, end, questionnaire, this.tz, filterBy);
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  getNPSCount(start: any, end: any, questionnaire: any, filterBy: any): Observable<any> {
    let endpoint = 'insights/exits/nps/count?'
        + InsightsService.getQueryStr(start, end, questionnaire, this.tz, filterBy);
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  getParticipationResponseCount(start: any, end: any, questionnaire: any, filterBy: any, type: string): Observable<any> {
    let endpoint;
    if ( type === 'pulses' ) {
      endpoint = 'insights/pulses/participation/response_count?';
    }
    else {
      endpoint = 'insights/exits/participation/response_count?';
    }
    endpoint += InsightsService.getQueryStr(start, end, questionnaire, this.tz, filterBy);
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  getPulseParticipationResponseCount(start: any, end: any, questionnaire: any, department: any, request: any): Observable<any> {
    let endpoint = 'insights/pulses/participation/response_count?';
    endpoint += InsightsService.getPulseQueryStr(start, end, questionnaire, this.tz, department, request);
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  getPerfOverall(start: any, end: any, questionnaire: any, filterBy: any): Observable<any> {
    let endpoint = `insights/exits/performance/overall?`
      + InsightsService.getQueryStr(start, end, questionnaire, this.tz, filterBy);

    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  getPulsePerfOverall(start: any, end: any, questionnaire: any, filterBy: any, department: any, request: any): Observable<any> {
    let endpoint = `insights/pulses/performance/overall?`
      + InsightsService.getPulseQueryStr(start, end, questionnaire, this.tz, department, request, filterBy, '');

    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  getPerfTrend(start: any, end: any, questionnaire: any, filterBy: any): Observable<any> {
    let endpoint = `insights/exits/performance/trend?`
    + InsightsService.getQueryStr(start, end, questionnaire, this.tz, filterBy);

    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  getPulsePerfTrend(start: any, end: any, questionnaire: any, filterBy: any, department: any, request: any): Observable<any> {
    let endpoint = `insights/pulses/performance/trend?`
      + InsightsService.getPulseQueryStr(start, end, questionnaire, this.tz, department, request, filterBy, '');

    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  getPerfMetrics(start: any, end: any, questionnaire: any, filterBy: any): Observable<any> {
    let endpoint = 'insights/exits/perf/trend?'
        + InsightsService.getQueryStr(start, end, questionnaire, this.tz, filterBy);
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  getPerfAll(start: any, end: any, questionnaire: any, filterBy: any): Observable<any> {
    let endpoint = 'insights/exits/perf/all?'
        + InsightsService.getQueryStr(start, end, questionnaire, this.tz, filterBy);
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  getParticipationOverall(start: any, end: any, questionnaire: any, filterBy: any, type: string, request?:any, department?:any): Observable<any> {
    let endpoint;
    if (type === 'pulse') {
      endpoint = 'insights/pulses/participation/overall?';
      endpoint += InsightsService.getPulseQueryStr(start, end, questionnaire, this.tz, department, request, filterBy);
    }
    else {
      endpoint = 'insights/exits/participation/overall?';
      endpoint += InsightsService.getQueryStr(start, end, questionnaire, this.tz, filterBy);
    }
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  getParticipationTrend(start: any, end: any, questionnaire: any, filterBy: any, state: any, type: string, request?: any, department?: any): Observable<any> {
    let endpoint;
    if (type === 'pulse') {
      endpoint = 'insights/pulses/participation/trend?';
      endpoint += 'state=' + state + '&' + InsightsService.getPulseQueryStr(start, end, questionnaire, this.tz, department, request, filterBy);
    }
    else {
      endpoint = 'insights/exits/participation/trend?';
      endpoint += 'state=' + state + '&' + InsightsService.getQueryStr(start, end, questionnaire, this.tz, filterBy);
    }
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  getReasonForLeaving(start: any, end: any, questionnaire: any, filterBy: any): Observable<any> {
    let endpoint = 'insights/exits/reasonForLeaving?'
        + InsightsService.getQueryStr(start, end, questionnaire, this.tz, filterBy);
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  getPulseInsightsMetrics(start: any, end: any, request?: any, department?: any, survey?: any): Observable<any> {
    let endpoint;
    endpoint = 'insights/pulses/performance/metrics?' + InsightsService.getPulseQueryStr(start, end, survey, this.tz, department, request, '', '')
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  getPulseDepartmentPerformanceScores(start: any, end: any, request?: any, department?: any, survey?: any) {
    let endpoint;
    endpoint = 'insights/pulses/performance/department?' + InsightsService.getPulseQueryStr(start, end, survey, this.tz, department, request, '', '')
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  getPulseNpsScoreCount(start: any, end: any, survey?:any, department?: any, request?: any): Observable<any> {
    let endpoint;
    endpoint = 'insights/pulses/nps/score_breakdown?' + InsightsService.getPulseQueryStr(start, end, survey, this.tz, department, request, '', '')
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }


  getPulsenpsBreakdown(start: any, end:any, department: any, survey:any, request: any ): Observable<any> {
    let endpoint;
    endpoint = 'insights/pulses/nps/response_breakdown?' + InsightsService.getPulseQueryStr(start, end, survey, this.tz, department, request, '', '');
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  getPulseNPSOverall(start: any, end: any, request?: any, department?: any, survey?: any): Observable<any> {
    let endpoint;
    endpoint = 'insights/pulses/nps/overall?' + InsightsService.getPulseQueryStr(start, end, survey, this.tz, department, request, '', '');
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  getPulseNPSTrend(start: any, end: any, request?: any, department?: any, survey?: any): Observable<any> {
    let endpoint;
    endpoint = 'insights/pulses/nps/trend?' + InsightsService.getPulseQueryStr(start, end, survey, this.tz, department, request, '', '');
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  getPulseComments(
    start: moment.Moment,
    end: moment.Moment,
    request: string = '',
    department: string = '',
    survey: string = '',
    limit: number = 20,
    page: number = 1
  ): Observable<any> {
    const queryString = InsightsService.getPulseCommentsQueryStr(
      start, end, survey, this.tz, department, request, limit, page
    );
    const endpoint = `insights/pulses/comments?${queryString}`;
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  getPulseQuestions(
    start: moment.Moment,
    end: moment.Moment,
    request: string = '',
    department: string = '',
    survey: string = ''
  ): Observable<any> {
    const queryString = InsightsService.getPulseQueryStr(
      start, end, survey, this.tz, department, request, '', ''
    );
    const endpoint = `insights/pulses/performance/questions?${queryString}`;
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  getPulseQuestionComments(
    start: moment.Moment,
    end: moment.Moment,
    request: string = '',
    department: string = '',
    survey: string = '',
    questionId: number,
    limit: number = 20,
    page: number = 1
  ): Observable<any> {
    let queryString = InsightsService.getPulseQueryStr(
      start, end, survey, this.tz, department, request, '', ''
    );
    if (limit > 0) {
      queryString += `&limit=${limit}&offset=${limit * (page - 1)}`;
    }
    const endpoint = `insights/pulses/performance/question/${questionId}/comments?${queryString}`;
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

}

